/** biome-ignore-all lint/complexity/noUselessFragments: useless rule */
import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    type ContentOwnership,
    compareContentOwners,
    isSoftDeletedSpace,
} from '@gitbook/app-database';
import { translationLanguageDescriptors } from '@gitbook/app-translations';
import { getApplicationEndpoint } from '@gitbook/env';
import {
    createPalette,
    intlPluralizeString,
    type PaletteCommandDef,
    type PaletteGroupDef,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    clipboard,
    Dialog,
    DialogActionButton,
    type DialogActionButtonProps,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    Heading,
    toast,
    useDialogClose,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useEventCallback, usePersistentValue } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../navigation';
import { apiClient, apiQuery } from '../../state';
import { capitalizeFirstLetter } from '../../utils';
import { createPickContentOwnerPalette } from '../Content';
import { useCreateInOwner } from '../ContentListing';
import { useOrganizationSites } from '../Organization';
import { createPickLanguagePalette } from '../Translations';
import { SpaceCreationDialog } from './creation';
import { UpdateSpaceTitleDialog } from './UpdateSpaceTitleDialog';

/**
 * Return common basic actions commands for a space.
 */
export function useSpaceActionsCommands(space: api.Space): {
    paletteGroup: PaletteGroupDef;
    elements: React.ReactNode;
} | null {
    const { navigate } = useNavigation();
    const copyPalette = useSpaceCopyCommands(space);
    const updateTitleTrigger = useDialogTriggerState();
    const [confirmDialogState, showConfirmDialog] = React.useState<ConfirmDialogState | null>(null);
    const { open: renameSpace } = updateTitleTrigger;

    const deleteSpace = useEventCallback(async () => {
        await toast.catch(async () => {
            await apiClient.spaces.deleteSpaceById(space.id);
            const toastId = toast.success('Space deleted', {
                description: 'You can restore this space within 7 days.',
                action: (
                    <Button
                        label="Undo"
                        onPress={async () => {
                            await toast.catch(async () => {
                                await apiClient.spaces.restoreSpace(space.id);
                                toast.dismiss(toastId);
                            }, 'Error while restoring space');
                        }}
                    />
                ),
            });
        }, 'Error while deleting space');
    });

    const moveSpace = useEventCallback(async (targetOwner: ContentOwnership) => {
        const promise = (async () => {
            if (space.organization === targetOwner.organization) {
                // Move space in the same organization
                await apiClient.spaces.moveSpace(space.id, {
                    parent: targetOwner.collection || null,
                });
            } else {
                // Transfer space to a different organization
                await apiClient.spaces.transferSpace(space.id, {
                    organization: targetOwner.organization,
                });
            }

            analytics.logEvent(
                analytics.createSpaceEvent(
                    space,
                    analytics.createContentMoveEvent(
                        {
                            organization: space.organization,
                            collection: space.parent,
                        },
                        targetOwner,
                        'palette',
                        {
                            type: 'space_move',
                        }
                    )
                )
            );
        })();

        toast.promise(promise, {
            loading: 'Moving space…',
            success: 'Space moved',
            error: 'Error while moving space',
        });

        return promise;
    });

    const paletteGroup = React.useMemo(() => {
        const commands = isSoftDeletedSpace(space)
            ? []
            : [
                  getSpaceLanguageCommands(space),
                  // Move Space
                  space.permissions.admin
                      ? {
                            key: 'space.moveTo',
                            icon: Icon.ArrowCurveUpRight,
                            title: 'Move space to…',
                            placeholder: 'No content found',
                            descendant: () => {
                                return createPickContentOwnerPalette({
                                    filter: (ancestors) =>
                                        ancestors[0]?.organization === space.organization,
                                    onPick: (owner) => {
                                        if (
                                            // Test if we are moving to the same location
                                            !compareContentOwners(
                                                {
                                                    organization: space.organization,
                                                    collection: space.parent,
                                                },
                                                owner,
                                                true
                                            )
                                        ) {
                                            if (owner.organization === space.organization) {
                                                moveSpace(owner);
                                            } else {
                                                showConfirmDialog({
                                                    space,
                                                    action: 'move',
                                                    onConfirm: async () => {
                                                        await moveSpace(owner);
                                                    },
                                                });
                                            }
                                        }
                                    },
                                });
                            },
                        }
                      : null,
                  // Copy commands
                  copyPalette,
                  // Duplicate
                  space.permissions.admin
                      ? {
                            key: 'space.duplicate',
                            icon: Icon.Duplicate,
                            title: 'Duplicate',
                            trigger: async () => {
                                await toast.catch(async () => {
                                    const { data: duplicatedSpace } =
                                        await apiClient.spaces.duplicateSpace(space.id);

                                    navigate({
                                        screen: {
                                            type: 'space',
                                            organization: duplicatedSpace.organization,
                                            space: duplicatedSpace.id,
                                        },
                                    });
                                }, 'Error while duplicating space');
                            },
                        }
                      : null,
                  space.permissions.admin
                      ? {
                            key: 'space.rename',
                            icon: Icon.Edit,
                            title: 'Rename',
                            trigger: renameSpace,
                        }
                      : null,
                  // Delete space
                  space.permissions.admin
                      ? {
                            key: 'space.delete',
                            icon: Icon.Trashcan,
                            title: 'Delete',
                            trigger: () => {
                                showConfirmDialog({
                                    space,
                                    action: 'delete',
                                    onConfirm: deleteSpace,
                                });
                            },
                        }
                      : null,
              ].filter(filterOutNullable);
        if (!commands.length) {
            return null;
        }
        return { key: 'space', commands };
    }, [space, copyPalette, moveSpace, navigate, deleteSpace, renameSpace]);

    if (!paletteGroup) {
        return null;
    }

    return {
        paletteGroup,
        elements: (
            <>
                <DialogRoot {...updateTitleTrigger.rootProps}>
                    <UpdateSpaceTitleDialog space={space} />
                </DialogRoot>
                <DialogRoot
                    isOpen={Boolean(confirmDialogState)}
                    onOpenChange={(isOpen) => {
                        if (!isOpen) {
                            showConfirmDialog(null);
                        }
                    }}
                >
                    <ConfirmDialog state={confirmDialogState} />
                </DialogRoot>
            </>
        ),
    };
}

/**
 * Return space language commands.
 */
export function getSpaceLanguageCommands(space: api.Space): PaletteCommandDef | null {
    return space.permissions.admin
        ? {
              key: 'space.language',
              icon: Icon.Globe,
              title: 'Language',
              subtitle: space.language
                  ? translationLanguageDescriptors[space.language].label
                  : undefined,
              descendant: () =>
                  createPickLanguagePalette({
                      initialValue: space.language ?? null,
                      onPick: async (language) => {
                          if (language !== space.language) {
                              analytics.logEvent(
                                  analytics.createSpaceEvent(space, {
                                      type: 'space_edit_language',
                                      language,
                                  })
                              );
                              await apiClient.spaces.updateSpaceById(space.id, {
                                  language,
                              });
                          }
                      },
                  }),
          }
        : null;
}

/**
 * Return space Copy commands.
 *
 * Mainly useful for development purposes.
 */
export function useSpaceCopyCommands(space: api.Space): PaletteCommandDef {
    const { getNavigationURL } = useNavigation();

    return React.useMemo(() => {
        return {
            key: 'space.copy',
            icon: Icon.Copy,
            title: 'Copy',
            descendant: () => {
                return createPalette({
                    static: true,
                    groups: [
                        {
                            key: 'space.copy',
                            commands: [
                                {
                                    key: 'space.copyLink',
                                    icon: Icon.Link,
                                    title: 'Copy link',
                                    trigger: () => {
                                        analytics.logEvent(
                                            analytics.createSpaceEvent(space, {
                                                type: 'space_copy_url',
                                            })
                                        );
                                        clipboard.writeText(
                                            `${getApplicationEndpoint()}${getNavigationURL({
                                                screen: {
                                                    type: 'space',
                                                    organization: space.organization,
                                                    space: space.id,
                                                },
                                            })}`
                                        );
                                    },
                                },
                                {
                                    key: 'space.copyId',
                                    icon: Icon.ID,
                                    title: 'Copy ID',
                                    trigger: () => {
                                        clipboard.writeText(space.id);
                                    },
                                },
                                {
                                    key: 'space.copyTitle',
                                    icon: Icon.Type,
                                    title: 'Copy title',
                                    trigger: () => {
                                        clipboard.writeText(space.title);
                                    },
                                },
                            ],
                        },
                    ],
                });
            },
        };
    }, [space, getNavigationURL]);
}

/**
 * Command to create spaces from a palette menu.
 */
export const useCreateSpaceCommands = (
    owner: ContentOwnership,
    site?: api.Site
): [Record<string, PaletteCommandDef | null>, React.ReactNode] => {
    const { createSpace } = useCreateInOwner(owner);
    const { navigate } = useNavigation();
    const spaceCreationDialogTrigger = useDialogTriggerState();

    const palette = React.useMemo(() => {
        return {
            newSpace: createSpace
                ? {
                      key: 'space_create',
                      icon: Icon.Space,
                      title: 'New space',
                      subtitle: 'A space for your content',
                      testID: 'sidebar.createSpaceButton',
                      trigger: () => {
                          spaceCreationDialogTrigger.setOpen(true);
                      },
                  }
                : null,
            newSpaceForSite:
                site && createSpace
                    ? {
                          key: 'new_space',
                          icon: Icon.Space,
                          title: 'New space',
                          subtitle: 'A blank space for your site content',
                          testID: 'siteContent.emptyState.createSpace',
                          trigger: async () => {
                              await toast.catch(async () => {
                                  const newSpace = await createSpace({
                                      withNavigate: false,
                                      withTitle: site.title,
                                  });

                                  await apiClient.siteSpaces.addSpaceToSite(
                                      owner.organization,
                                      site.id,
                                      {
                                          spaceId: newSpace.id,
                                      }
                                  );

                                  navigate({
                                      screen: {
                                          type: 'space',
                                          space: newSpace.id,
                                      },
                                  });
                              }, 'Error adding space to site');
                          },
                      }
                    : null,
        };
    }, [createSpace, navigate, owner, site, spaceCreationDialogTrigger]);

    return [
        palette,
        createSpace ? (
            <DialogRoot key="create-space-dialog" {...spaceCreationDialogTrigger.rootProps}>
                <SpaceCreationDialog
                    organizationId={owner.organization}
                    collectionId={owner.collection}
                />
            </DialogRoot>
        ) : null,
    ];
};

function MultipleSitesAlert(props: {
    siteItems: api.OrganizationAllSite[];
    message: (props: { spaceSites: api.Site[] }) => React.ReactNode;
}) {
    const { siteItems } = props;
    const closeDialog = useDialogClose();

    if (!siteItems.length) {
        return null;
    }

    const sites = siteItems.map((siteItem) => siteItem.site);

    return (
        <Alert
            kind="danger"
            size="small"
            title={`This space is linked to ${intlPluralizeString({
                count: sites.length,
                singular: 'a site',
                plural: 'multiple sites',
            })}`}
            details={
                <div className="flex flex-col gap-2">
                    <span>{props.message({ spaceSites: sites })}</span>
                    <SpaceSitesList spaceSites={sites} onNavigate={closeDialog} />
                </div>
            }
        />
    );
}

/**
 * Render a simple list of sites where a space has a link to.
 */
export function SpaceSitesList(props: {
    spaceSites: api.Site[];
    onNavigate?: () => void;
}): React.ReactElement {
    const { spaceSites, onNavigate } = props;

    const { navigate } = useNavigation();

    return (
        <div className="flex flex-wrap gap-2">
            {spaceSites.map((spaceSite) => {
                const { id: key, title: label } = spaceSite;

                return (
                    <Button
                        key={key}
                        kind="secondary"
                        size="small"
                        leadingIcon={Icon.DocsSiteLink}
                        onPress={() => {
                            navigate({
                                screen: { type: 'site', site: key },
                            });

                            onNavigate?.();
                        }}
                        label={label}
                    />
                );
            })}
        </div>
    );
}

/**
 * This dialog is displayed to the user when they attempt to delete or move a space.
 * It may show a confirmation dialog or an alert dialog informing the user they can't
 * perform the action when the given space is referenced by a site.
 */
type ConfirmDialogState = {
    space: api.Space;
    action: 'delete' | 'move';
    onConfirm: DialogActionButtonProps['onPress'];
};

function ConfirmDialog(props: { state: ConfirmDialogState | null }) {
    const { state } = props;

    const persistentState = usePersistentValue(state);

    if (!persistentState) {
        return null;
    }

    return (
        <Dialog>
            <ConfirmDialogContent state={persistentState} />
        </Dialog>
    );
}

function ConfirmDialogContent(props: { state: ConfirmDialogState }) {
    const { state } = props;

    const { data: organizationContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId: state.space.organization,
        })
    );

    const siteItems = useOrganizationSites(
        organizationContent ?? { sites: [], spaces: [], deletedSpaces: [] },
        { spaceId: state.space.id }
    );

    const isActionForbidden = siteItems.length > 0;

    return (
        <>
            <DialogHeader>
                <Heading>
                    {isActionForbidden
                        ? `Cannot ${state.action} space`
                        : `${capitalizeFirstLetter(state.action)} space`}
                </Heading>
            </DialogHeader>
            <DialogBody>
                {isActionForbidden ? (
                    <MultipleSitesAlert
                        siteItems={siteItems}
                        message={({ spaceSites }) => (
                            <>
                                You won’t be able to {state.action} it until it's removed from the{' '}
                                {intlPluralizeString({
                                    count: spaceSites.length,
                                    singular: 'site',
                                    plural: 'sites',
                                })}{' '}
                                below.
                            </>
                        )}
                    />
                ) : state.action === 'delete' ? (
                    <>
                        This space and all of its content will be deleted. Deleted spaces can be
                        restored from the trash for up to seven days.
                    </>
                ) : (
                    <>This space and all of its content will be moved.</>
                )}
            </DialogBody>
            <DialogFooter>
                {isActionForbidden ? (
                    <DialogCloseButton label="Ok" />
                ) : (
                    <>
                        <DialogCloseButton />
                        <DialogActionButton
                            label={capitalizeFirstLetter(state.action)}
                            onPress={state.onConfirm}
                            kind="danger"
                        />
                    </>
                )}
            </DialogFooter>
        </>
    );
}
